import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface GalleryImage {
  name: string;
  url: string;
}

const IMAGE_RE = /\.(jpe?g|png|webp|gif)$/i;
const PAGE_SIZE = 6;
const EXCLUDED = new Set(['logo.jpg']);
const BUCKET = 'Glass-railing-images';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const Gallery = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from(BUCKET)
          .list('', { limit: 200, offset: 0, sortBy: { column: 'created_at', order: 'desc' } });

        if (error) throw error;

        const result: GalleryImage[] = [];
        for (const file of files ?? []) {
          if (!file.name || !IMAGE_RE.test(file.name)) continue;
          if (EXCLUDED.has(file.name)) continue;
          const { data } = supabase.storage.from(BUCKET).getPublicUrl(file.name);
          result.push({ name: file.name, url: data.publicUrl });
        }

        setImages(shuffle(result));
      } catch (err) {
        console.error('[Gallery] Failed to load images:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);

  const prev = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);

  const next = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i + 1) % images.length));
  }, [images.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, prev, next]);

  const formatName = (name: string) =>
    name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const totalPages = Math.ceil(images.length / PAGE_SIZE);
  const pageImages = images.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section id="gallery" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="h-px w-8" style={{ backgroundColor: '#e8ad1a' }} />
            <span className="section-label">Gallery</span>
            <div className="h-px w-8" style={{ backgroundColor: '#e8ad1a' }} />
          </div>
          <h2 className="section-title mb-4">Gallery & Past Projects</h2>
          <p className="text-slate-500 max-w-xl mx-auto text-base leading-relaxed">
            Browse our full collection of completed projects and installations.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-slate-300" />
          </div>
        ) : images.length === 0 ? (
          <p className="text-center text-slate-400 py-20">No images available.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pageImages.map((img, i) => {
                const globalIndex = page * PAGE_SIZE + i;
                return (
                  <div
                    key={`${img.name}-${globalIndex}`}
                    className="group relative overflow-hidden rounded-2xl cursor-pointer border border-slate-100"
                    style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', aspectRatio: '4/3' }}
                    onClick={() => openLightbox(globalIndex)}
                  >
                    <img
                      src={img.url}
                      alt={formatName(img.name)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 transition-all duration-300 flex items-center justify-center">
                      <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-xs font-medium truncate">{formatName(img.name)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-10">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-slate-400 text-sm">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(2,6,18,0.96)' }}
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors p-2 rounded-xl hover:bg-white/10"
            onClick={closeLightbox}
          >
            <X className="w-6 h-6" />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-3 rounded-xl hover:bg-white/10"
            onClick={e => { e.stopPropagation(); prev(); }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div
            className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center gap-3"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex].url}
              alt={formatName(images[lightboxIndex].name)}
              className="max-h-[78vh] max-w-full w-auto object-contain rounded-xl"
              style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }}
            />
            <p className="text-white/50 text-sm font-medium">{formatName(images[lightboxIndex].name)}</p>
            <p className="text-white/25 text-xs">{lightboxIndex + 1} / {images.length}</p>
          </div>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-3 rounded-xl hover:bg-white/10"
            onClick={e => { e.stopPropagation(); next(); }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
